// api/starGardenApi.js – Star Garden backend API helpers.
import { fetchJson, apiUrl } from '../../../apiBase';

/* ── Board (main game state) ──────────────────────────────── */

/**
 * Fetch the full board: criteria + student progress for a class.
 * @param {string|number} schoolId
 * @param {string|number} classId  numeric class id
 * @returns {Promise<{criteria: Array, students: Array}>}
 */
export async function fetchBoard(schoolId, classId) {
  const res = await fetchJson(
    apiUrl(`/admin/star-garden/board/${encodeURIComponent(schoolId)}/${encodeURIComponent(classId)}`)
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/* ── Results / reporting ─────────────────────────────────── */

/**
 * Fetch aggregated star/criteria results for a class.
 * @param {string|number} schoolId
 * @param {string|number} classId
 * @returns {Promise<{class_totals: Array, criteria: Array, students: Array}>}
 */
export async function fetchResults(schoolId, classId) {
  const res = await fetchJson(
    apiUrl(
      `/admin/star-garden/results/${encodeURIComponent(schoolId)}/${encodeURIComponent(classId)}`
    )
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/* ── Actions ──────────────────────────────────────────────── */

/**
 * Award a star to a student for a criteria.
 * @returns {Promise<{student: Object}>} updated student progress
 */
export async function awardStar(schoolId, { studentId, criteriaId, classId }) {
  const res = await fetchJson(
    apiUrl(`/admin/star-garden/action/${encodeURIComponent(schoolId)}`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        student_id: studentId,
        criteria_id: criteriaId,
        class_id: classId,
      }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Undo the last action for a student.
 * @returns {Promise<{student: Object, deleted_action_id: number|null}>}
 */
export async function undoLastAction(schoolId, studentId) {
  const res = await fetchJson(
    apiUrl(
      `/admin/star-garden/action/undo/${encodeURIComponent(schoolId)}/${encodeURIComponent(studentId)}`
    ),
    { method: 'DELETE' }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Award stars to multiple students using one criteria and class context.
 * @param {string|number} schoolId
 * @param {{classId: string|number, criteriaId: number, studentIds: Array<string|number>}} payload
 * @returns {Promise<{batch_id?: string|number, updated_students?: Array, failed_students?: Array}>}
 */
export async function awardStarBatch(schoolId, { classId, criteriaId, studentIds }) {
  const res = await fetchJson(
    apiUrl(`/admin/star-garden/action/batch/${encodeURIComponent(schoolId)}`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        class_id: classId,
        criteria_id: criteriaId,
        student_ids: studentIds,
      }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Undo the latest (or provided) batch action in a class.
 * @param {string|number} schoolId
 * @param {{classId: string|number, batchId?: string|number|null}} payload
 * @returns {Promise<{reverted_students?: Array, reverted_batch_id?: string|number|null}>}
 */
export async function undoLastBatchAction(schoolId, { classId, batchId = null }) {
  const res = await fetchJson(
    apiUrl(`/admin/star-garden/action/batch/undo/${encodeURIComponent(schoolId)}`),
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        class_id: classId,
        batch_id: batchId,
      }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/* ── Criteria CRUD ────────────────────────────────────────── */

export async function fetchCriteria(schoolId, classId = null) {
  const qs = classId ? `?class_id=${encodeURIComponent(classId)}` : '';
  const res = await fetchJson(
    apiUrl(`/admin/star-garden/criteria/${encodeURIComponent(schoolId)}${qs}`)
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function createCriteria(schoolId, { classId = null, name, icon, sortOrder }) {
  const res = await fetchJson(
    apiUrl(`/admin/star-garden/criteria/${encodeURIComponent(schoolId)}`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        class_id: classId || null,
        name,
        icon: icon || null,
        sort_order: sortOrder || 99,
      }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function deleteCriteria(schoolId, criteriaId) {
  const res = await fetchJson(
    apiUrl(
      `/admin/star-garden/criteria/${encodeURIComponent(schoolId)}/${encodeURIComponent(criteriaId)}`
    ),
    { method: 'DELETE' }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function updateCriteria(schoolId, criteriaId, { name = null, icon = null, sortOrder = null }) {
  const res = await fetchJson(
    apiUrl(
      `/admin/star-garden/criteria/${encodeURIComponent(schoolId)}/${encodeURIComponent(criteriaId)}`
    ),
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        icon,
        sort_order: sortOrder,
      }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/* ── Classes ──────────────────────────────────────────────── */

/**
 * Fetch classes for a school (reuses existing admin endpoint).
 * Returns array of { id, name, ... }.
 */
export async function fetchClasses(schoolId) {
  const res = await fetchJson(
    apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`)
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
